import { toast } from 'sonner'
import type { CommandRequest, CommandResponse } from '@gridfit/contracts/wire'
import { ApiError, runCommand } from '../api/client'
import { noteRefusedAdded, noteRowRemoved, notifyOutboxChanged, outboxDb } from './db'
import { pending } from './enqueue'
import type { OutboxEntry } from './db'

export interface DrainResult {
  readonly sent: number
  readonly refused: number
}

/**
 * The in-flight sweep, if one is currently running — review round 1's
 * CRITICAL, fixed here after being measured. `onOnline` and
 * `onVisibilityChange` (`startOutboxAutoDrain`, below) both call `drain()`
 * with nothing serialising them, and BOTH events fire together on exactly
 * this feature's own use case — a phone regaining signal fires `online`
 * AND, the moment the tab is looked at again, `visibilitychange` to
 * `'visible'`. Measured before this guard existed, counting MSW request
 * starts: two queued rows produced FOUR POSTs, one pair per overlapping
 * sweep, each sweep having read the same `pending()` snapshot before either
 * had deleted anything. The ledger stayed correct only because the
 * appender's dedupe absorbed the duplicate sends — this guard is what makes
 * that a NON-EVENT instead of the mechanism the correctness depends on.
 *
 * The worse half, also measured: `noteRowRemoved()` (`db.ts`) firing TWICE
 * per row from the two overlapping sweeps corrupted the synchronous
 * `isOutboxEmpty()` mirror independently of the ledger — with three queued
 * rows where the third send failed mid-sweep (a reconnect that drops again,
 * the realistic rural-site case), `isOutboxEmpty()` read `true` while a
 * row was still genuinely `queued`, which is precisely the seam Task 16
 * built and this task exists to fill, returning the WRONG answer: a kiosk
 * would auto-accept an update and reload with unsent work still in the
 * table, stranding that row's `clientBuild` behind the floor the new build
 * no longer accepts (`db.ts`'s own `rowCount` comment names this exact
 * failure).
 *
 * `??=` — a second `drain()` call while one is already running returns the
 * SAME promise the first call is waiting on, rather than starting a second
 * sweep against a `pending()` snapshot the first sweep may already be
 * mutating. `.finally` clears this once the sweep settles (success OR
 * failure), so the NEXT trigger — whether that is a fresh `online` event or
 * simply the next call after this one resolves — starts a genuinely new
 * sweep rather than being stuck replaying a stale promise forever.
 *
 * It is not enough on its own, and never could be: this is per-tab MODULE
 * state over an IndexedDB every tab of the origin shares. `exclusively`
 * (below) is the cross-tab half.
 */
let inFlight: Promise<DrainResult | null> | null = null

/**
 * Replays every queued command, oldest first, sequentially (design §8.2).
 * "Sequentially", not `Promise.all` — a later command in the same outbox
 * may depend on an earlier one's effect having already landed (the toy
 * drill's own `retire ... thenBumpBy` shape is exactly this kind of
 * dependency), and sending them out of order or concurrently could replay
 * them against state the earlier one had not yet moved. See `inFlight`'s
 * own comment above for why THIS function is also serialised against
 * ITSELF — sequential sends inside one sweep is not enough if two sweeps
 * can run at once.
 *
 * `entry.idempotencyKey` is sent AS STORED — never re-minted here. This is
 * the other half of `enqueue.ts`'s own safety note: the appender's
 * `command_log` dedupe recognises a replay only because the key on the wire
 * is byte-identical to the one it saw the first time. Minting a fresh key
 * per send would make every drain indistinguishable from a brand new
 * command, and at-least-once delivery would stop being safe — this is
 * exactly what `test/outbox.test.tsx`'s "draining the same entry twice
 * appends one event" case measures, and the only place that property is
 * visible (`cell.events().length`).
 *
 * `observing: []` always — `wire.ts`'s own comment: "an offline outbox
 * drain has no live cache to refresh". The screen that originally queued
 * this command may not even be mounted by the time it drains.
 *
 * `null` — not `{ sent: 0, refused: 0 }` — when another TAB holds the lock
 * (`exclusively`, below). A zeroed result would be a value claiming nothing
 * was sent while another tab was sending, and the first "Sync now" button
 * would render it as a report.
 */
export function drain(): Promise<DrainResult | null> {
  inFlight ??= exclusively(sweep).finally(() => {
    inFlight = null
  })
  return inFlight
}

/** The name every tab of this origin competes for — Web Locks are scoped to the origin's storage bucket. */
const DRAIN_LOCK = 'gridfit-outbox-drain'

/**
 * Runs `work` only if no OTHER TAB is already sweeping. `inFlight` (above)
 * serialises this tab against itself and cannot see a second tab at all, so
 * without this both tabs sweep the same `pending()` snapshot — duplicate
 * POSTs, and `markRetry` writes racing each other on the rows. Server
 * idempotency (`command_log`) absorbs the duplicates, and that is exactly the
 * problem: it must not be the only defence on a money-bearing path.
 *
 * `ifAvailable: true` hands the loser `null` immediately rather than making it
 * queue to re-sweep rows the winner has by then already sent. WHOLE-SWEEP, not
 * per-row: a per-row claim would let two tabs interleave sends, breaking the
 * ordering `sweep()` preserves. Never `steal` — stealing IS the double sweep
 * this exists to prevent. What the loser gives up, said rather than left to
 * the benign half: a row IT enqueued after the winner's `pending()` snapshot
 * is in neither sweep, so it waits for the next `online`/`visibilitychange`
 * trigger. Durable, not lost — but not now.
 *
 * No `locks` at all ⇒ run unlocked, for jsdom; unreachable in production (Web
 * Locks needs the same secure context the service worker already does). The
 * accessor is typed optional deliberately: `lib.dom.d.ts` declares `readonly
 * locks: LockManager`, NON-optional, so a bare `if (!navigator.locks)` would
 * be a truthiness check against a type claiming it cannot be absent. `Omit`
 * first — intersecting a required property with an optional one leaves it
 * required.
 */
async function exclusively(work: () => Promise<DrainResult>): Promise<DrainResult | null> {
  const locks = (navigator as Omit<Navigator, 'locks'> & { locks?: LockManager }).locks
  if (!locks) return work()
  return await locks.request(DRAIN_LOCK, { ifAvailable: true }, async (lock) => (lock === null ? null : await work()))
}

async function sweep(): Promise<DrainResult> {
  const entries = await pending()
  let sent = 0
  let refused = 0

  for (const entry of entries) {
    // Final review bucket 1 item 5: a row a PREVIOUS sweep left queued
    // after a recoverable failure (`markRetry`, below) is skipped entirely
    // — not attempted, not counted either way — until its own backoff
    // elapses. See `isBackingOff`'s own doc comment for why.
    if (isBackingOff(entry)) continue

    const request: CommandRequest = {
      idempotencyKey: entry.idempotencyKey,
      clientBuild: entry.clientBuild,
      payload: entry.payload,
      observing: [],
    }

    let response: CommandResponse
    try {
      response = await runCommand(entry.command, request)
    } catch (error) {
      if (error instanceof ApiError) {
        // Review round 1's Important 2 (fixed here after being measured):
        // the server SAW this request and answered with a fault status —
        // either a PERMANENT one (a stale `clientBuild` 426, a payload
        // that no longer parses, an authorization the principal will never
        // regain — including, since web-pre-deploy item 4, a 401 whose body
        // says the ACCOUNT itself is disabled) or a RECOVERABLE one
        // (ordinarily 401/408/429/5xx — see `isRecoverableHttpFailure`'s own
        // doc comment for the exact split). Either way it is a
        // decision about THIS entry, not evidence the connection itself is
        // down, so neither must stall every entry queued behind it —
        // measured under the old `break`-on-any-error shape: a single 4xx
        // left `{ sent: 0, refused: 0 }`, both rows still `queued`, and a
        // second `drain()` repeated identically forever.
        //
        // FINAL REVIEW bucket 1 item 5, fixed here after being measured:
        // the shape that REPLACED that `break` — marking every `ApiError`
        // `refused`, no matter the status — was itself wrong for the
        // RECOVERABLE half. Concrete failure it produced: a site manager
        // works offline all afternoon, their session cookie expires, they
        // reconnect — auto-drain fires, every queued row gets 401
        // `{"error":"unauthenticated"}`, every row became `refused`
        // PERMANENTLY (nothing here retries a `refused` row on its own),
        // rendering the literal word "unauthenticated" under "Needs your
        // attention" beside a Resend button that would fail identically
        // forever, with nothing on screen saying "sign in again and this
        // clears itself". A transient 502, or the worker's own `{error:
        // 'internal error'}` 500, did the same. Split now: a recoverable
        // status leaves the row QUEUED (`markRetry`) so it drains on its
        // own the moment the underlying condition clears; only a genuinely
        // PERMANENT status becomes a human-repairable `refused` entry, and
        // its message is a synthesised SENTENCE (`humanSentenceForHttpFailure`),
        // never the raw wire string — `RefusalReason`'s own doc comment
        // promises "the complete, human-readable sentence the gate itself
        // produced", which was false of `error.message` for these
        // synthesised (non-`defineRefusal`) refusals before this fix.
        if (isRecoverableHttpFailure(error)) {
          await markRetry(entry)
          continue
        }
        await markRefused(entry, [
          { code: `http-${error.status}`, message: humanSentenceForHttpFailure(error.status, error.reason) },
        ])
        refused += 1
        continue
      }
      // A genuine transport fault — `fetch` itself rejected, no response at
      // all: the connection dropped again mid-drain, or `client.ts`'s
      // `REQUEST_TIMEOUT_MS` elapsed with the request accepted and never
      // answered (a `TimeoutError` `DOMException`, deliberately not an
      // `ApiError`, so it lands here and not in the branch above). This is the ONLY
      // case that stops the sweep: every LATER entry would fail identically
      // right now, and unlike an `ApiError`, there is no per-entry decision
      // to record — leaving this entry and everything after it `queued`, in
      // order, for the next `online`/`visibilitychange` trigger to retry
      // from the top.
      break
    }

    if (response.ok) {
      await outboxDb().entries.delete(entry.idempotencyKey)
      noteRowRemoved()
      sent += 1
    } else {
      // A business refusal/abstention (architecture's "park, never guess") —
      // the server saw this command and answered no via an ordinary 200.
      // EVERY failure, not just the first (fix round 1's Important 3, fixed
      // after being measured) — `combineGates` (core/gates) can return
      // several at once, and the online path (`test/data-layer.test.tsx`'s
      // `DraftScreen`) already renders all of them; a tray showing fewer
      // than the online path for the SAME response sends a user into a
      // repair loop, fixing reason 1, resending, only then discovering
      // reason 2.
      await markRefused(
        entry,
        response.failures.map((failure) => ({ code: failure.refusal.code, message: failure.refusal.message })),
      )
      refused += 1
    }
  }

  return { sent, refused }
}

/**
 * Marks a row `refused` rather than deleting it — Task 18's Sync tray is
 * what a human uses to review and resolve it; nothing here retries it on
 * its own. `noteRowRemoved()` is called here too, not just on delete: a
 * `refused` row is no longer `queued`, so it must leave the synchronous
 * mirror `isOutboxEmpty()` reads the SAME way a deleted row does — see
 * `db.ts`'s own `rowCount` comment for why counting a `refused` row as
 * "not empty" is the exact bug review round 1's Important 2 measured.
 *
 * `noteRefusedAdded()` (Task 18) is the OTHER half: the row now counts
 * toward the Sync tray's OWN badge — a `refused` row is not "empty" from
 * the tray's point of view even though it must be from `isOutboxEmpty()`'s.
 *
 * `announceRefusal` (below) fires a toast — a COURTESY, never the record.
 * The record is this `put()` call: it lands in Dexie regardless of whether
 * anyone is looking at a screen right now, and survives the toast being
 * dismissed, reloaded past, or never seen at all (task brief: "the tray
 * entry must survive the toast being dismissed").
 *
 * EXPORTED — web-pre-deploy items 1 and 2, closed as the ONE fix they
 * actually are. `repairs.registry.tsx`'s `ToyTallyBumpRepair` is the SECOND
 * caller: when a repair's own resubmission is refused again, it persists
 * every one of `result.failures` through THIS function rather than keeping
 * a second, component-local `useState` copy of "how a refusal is recorded".
 * `refusals` is already plural by design (`db.ts:38`, task 18's Important
 * 3) and `RefusedEntry` (`repair.tsx`) already renders every one of them —
 * the repair path re-implementing its own single-reason state was the ONLY
 * divergence from that, and reimplementing it a second time in the registry
 * (rather than calling this function) would only move the divergence, not
 * delete it.
 *
 * `options.resend` — `true` for that second caller, `false`/omitted for
 * `sweep()`'s own drain-time call below. A resend refuses a row that is
 * ALREADY `refused`: this call's own `put()` only ever changes `refusals`
 * (status is already `'refused'` and stays that way), so the row was never
 * `queued` at the moment this runs, and it is already counted in
 * `refusedCount` from its ORIGINAL refusal. Running `noteRowRemoved()` /
 * `noteRefusedAdded()` again for the SAME physical row would drift both
 * mirrors — `rowCount` would go negative (this row leaving `queued` was
 * already accounted for once) and `refusedCount` would count one row as
 * two, exactly the "double-count the badge" this task's own brief warns
 * against — so both are skipped on this path.
 *
 * The Sync tray's own list still needs telling, though: a resend changes
 * this row's CONTENT (a new set of reasons) with NO change to either
 * count, and `SyncTray.tsx`'s fetch effect depends on `countsStore`'s
 * snapshot REFERENCE precisely so a content-only change like this one still
 * triggers a refetch (see that file's own comment) — `notifyOutboxChanged()`
 * is called directly here for that reason, republishing the SAME two
 * numbers under a fresh object.
 *
 * `announceRefusal` is skipped on this path too — its own doc comment's
 * "open the sync tray to see what needs your attention" makes no sense to
 * someone who is, right now, looking at the exact row it would describe;
 * the durable record (this function's own `put()`, above) is what the
 * tray's list reads, not the toast.
 *
 * CRITICAL, fixed here after being measured (review round 1 of this task):
 * the resend branch used to `put()` unconditionally, with no guard against
 * the row having been DISMISSED while the resend was still in flight.
 * Nothing disables Resend or Dismiss while a resubmission is pending, and on
 * a slow connection — this app's normal operating condition, not an edge
 * case — an impatient Dismiss before the response lands is an ordinary
 * sequence: `discard()` (`enqueue.ts`) deletes the row and calls
 * `noteRefusedRemoved()` (`refusedCount` back to the true `0`), and the
 * resend's own `put()` landing AFTERWARDS silently RESURRECTED that row —
 * Dexie held a `refused` row (visible in the tray) that `refusedCount` said
 * did not exist (invisible on the badge). Measured: a SECOND Dismiss on that
 * resurrected row then took `refusedCount` to `-1` (`db.ts:386`'s own
 * comment: deliberately unclamped), and every GENUINE refusal for the rest
 * of the session only brought the count back up to `0` — the badge staying
 * invisible for good, defeating exactly the guarantee it exists for ("a
 * phone in a pocket that missed the toast"). Confirmed newly introduced by
 * THIS task, not pre-existing: with the resend's `markRefused` call removed
 * entirely (`befead2`'s behaviour), the identical sequence leaves zero rows
 * in Dexie and no negative count — there was nothing to resurrect before a
 * resend could write.
 *
 * Fixed the SAME shape `enqueue.ts`'s own `discard()` already established
 * for the identical class of race (two concurrent mutations against one
 * row): a single Dexie `'rw'` transaction wrapping the `get()` GUARD and the
 * `put()`, rather than inventing a second pattern. A `'readwrite'`
 * transaction against `entries` cannot interleave with another `'readwrite'`
 * transaction against the SAME store, so a `discard()` that commits first
 * leaves this transaction's own `get()` seeing the row already gone —
 * `if (!current) return` then skips the `put()` (and the notify) entirely,
 * exactly the "park, never guess: nothing left to resend into" outcome
 * `test/sync-tray.test.tsx`'s own covering case pins.
 */
export async function markRefused(
  entry: OutboxEntry,
  refusals?: readonly { code: string; message: string }[],
  options?: { readonly resend?: boolean },
): Promise<void> {
  if (options?.resend) {
    const db = outboxDb()
    await db.transaction('rw', db.entries, async () => {
      const current = await db.entries.get(entry.idempotencyKey)
      // Dismissed while this resend was still in flight — there is no row
      // left to write the new refusals onto, and writing one back in would
      // resurrect exactly what `discard()` already removed (see this
      // function's own doc comment above for the measured corruption that
      // caused).
      if (!current) return
      await db.entries.put({ ...current, status: 'refused', ...(refusals ? { refusals } : {}) })
      notifyOutboxChanged()
    })
    return
  }
  await outboxDb().entries.put({ ...entry, status: 'refused', ...(refusals ? { refusals } : {}) })
  noteRowRemoved()
  noteRefusedAdded()
  announceRefusal(entry, refusals)
}

/**
 * The toast half of a drain-time refusal (task brief: "a toast appears when
 * the app is foregrounded at the moment it happens"). Gated on
 * `document.visibilityState` — the SAME signal `startOutboxAutoDrain`'s own
 * `visibilitychange` trigger already reads (below) — so a refusal produced
 * by a background tab's `online` event (the realistic case: a phone regains
 * signal in a pocket, screen off) does not pop a toast nobody is looking at;
 * it still lands in the tray via `markRefused`'s own `put()` above, which
 * runs regardless of this check.
 *
 * Reuses the FIRST refusal's OWN message verbatim — the same "no second
 * vocabulary for the same gate reasons" rule the tray itself follows
 * (`outbox/repair.tsx`'s `RefusalReason`); this is plain text, not that
 * component, because a toast is ephemeral chrome with no repair form to
 * host, but the WORDS are never re-authored here. Only the first, even when
 * `refusals` holds several — deliberately deferred, not an oversight: a
 * one-line-per-reason toast (or a "2 reasons" summary) is real UI work this
 * fix round's review named explicitly as OUT of scope ("one toast per
 * refused entry rather than a summary"). The DURABLE record (this
 * function's caller, `markRefused`'s own `put()`) carries every reason
 * regardless of what the toast shows — the tray, not the toast, is where a
 * user sees the rest.
 *
 * `document`/`toast` are both browser-only — safe to call unconditionally
 * because `drain.ts` only ever runs in `packages/web` (never core, never a
 * Worker), where `document` always exists once a page has loaded.
 */
function announceRefusal(entry: OutboxEntry, refusals?: readonly { code: string; message: string }[]): void {
  if (document.visibilityState !== 'visible') return
  toast.warning(refusals?.[0]?.message ?? `${entry.command} was refused.`, {
    description: 'Open the sync tray to see what needs your attention.',
  })
}

/**
 * Final review bucket 1 item 5 — splits a caught `ApiError`'s status (and,
 * for a 401 specifically, its `reason`) into RECOVERABLE (this function,
 * `true`) and PERMANENT (`false`, `humanSentenceForHttpFailure` below).
 * Recoverable means "the identical request MAY genuinely succeed later,
 * without a human doing anything to this specific queued entry":
 *
 *  - `401` with no `reason` — the ORIGINAL half of this bucket, and still
 *    the common case: the session cookie expired, or was never valid to
 *    begin with (a forged/unknown cookie), or the session resolved but
 *    `f_principals` itself had no row for it — `packages/api`'s own
 *    resolver maps a MISSING row to the identical bare `{ error:
 *    'unauthenticated' }` a plain expired cookie gets, deliberately: that
 *    row can be transiently absent while `ensureFoldShapes` rebuilds it
 *    from the ledger, and a rebuild window must not become a permanent
 *    refusal that discards queued work. Either way, a fresh sign-in (no
 *    change to the entry itself) makes the SAME request succeed.
 *  - `408`/`429` — the request timed out or was rate-limited; nothing
 *    about the entry was wrong, only the timing.
 *  - any `5xx` — a server-side fault, not a decision about this entry at
 *    all.
 *
 * web-pre-deploy item 4's client half narrows that first bullet: a `401`
 * WITH `reason: 'disabled'` is now PERMANENT, not recoverable. That reason
 * only appears when the session genuinely resolved and the principal row
 * itself said `disabled === 1` — a revoked account, not an expired one. No
 * fresh sign-in clears this; the identical request 401s identically
 * forever, which is exactly why leaving it in this bucket was the bug:
 * the row backed off on a doubling cadence, capped at 30 minutes, under
 * "Waiting to send" — indefinitely, never surfacing to a human the way a
 * permanent 4xx does.
 *
 * EVERYTHING ELSE that reaches here is treated as permanent, including
 * `426` — NOT named in the review's own worked examples (400, 403, 404,
 * 409, 422), but the same reasoning applies: a below-floor `clientBuild`
 * will 426 identically on every retry until the CLIENT changes (a new
 * build installs), which is exactly why Task 17's original fix marked a
 * 426 `refused` rather than leaving it queued forever — see `sweep()`'s own
 * comment for the closed-loop failure that shape exists to break (426 →
 * queued → outbox never empties → device auto-accept never fires → the
 * build update that would clear the floor never installs). Leaving 426 in
 * the "recoverable, stay queued" bucket would silently reopen that exact
 * loop.
 */
function isRecoverableHttpFailure(error: ApiError): boolean {
  if (error.status === 401) return error.reason !== 'disabled'
  return error.status === 408 || error.status === 429 || error.status >= 500
}

/**
 * The human sentence `RefusalReason`'s own doc comment promises ("the
 * complete, human-readable sentence the gate itself produced") — final
 * review bucket 1 item 5. Before this fix, EVERY caught `ApiError` reached
 * `markRefused` with `message: error.message`, which for a genuine server
 * refusal IS the raw wire string (`worker.ts`'s own `c.json({ error:
 * '...' })` bodies: `'unauthenticated'`, `'forbidden'`, ...) — a developer
 * identifier verbatim under "Needs your attention", the half of
 * cross-cutting finding 4 ("no developer identifiers on screen") that
 * survived Task 18's own fix to the entry LABEL. Only ever called for the
 * PERMANENT bucket (`isRecoverableHttpFailure` returns `false`) — a
 * recoverable failure never becomes a refusal at all; see `markRetry`.
 *
 * `reason` is threaded through for exactly one case: a `401` reaches this
 * function ONLY when `isRecoverableHttpFailure` decided it was PERMANENT,
 * which today means `reason === 'disabled'` (a plain expired-session 401
 * never gets here at all — it stays queued via `markRetry`). Read
 * defensively here rather than assumed: this switches on `reason` for that
 * one case instead of quietly reusing the generic `default` sentence, so a
 * FUTURE 401 cause this repo has not invented yet still falls through to
 * `default` rather than being misdescribed as a revoked account.
 *
 * The six explicit cases are the review's own worked examples (400, 403,
 * 404, 409, 422) plus 426 (see `isRecoverableHttpFailure`'s own comment for
 * why it belongs in the permanent bucket too) plus, added by web-pre-deploy
 * item 4, 401's `disabled` reason. The `default` case is a park-never-guess
 * fallback for any OTHER permanent status (or unrecognised 401 reason) this
 * worker does not currently return for `/api/cmd/*` — deliberately generic
 * rather than echoing `error.message` again, which would silently reopen the
 * exact problem this function exists to close for a status nobody has named
 * yet.
 */
function humanSentenceForHttpFailure(status: number, reason?: string): string {
  switch (status) {
    case 400:
      return 'This entry could not be understood by the server and cannot be sent as it is.'
    case 401:
      if (reason === 'disabled') {
        return 'This account no longer has access. Ask whoever manages your accounts to have it restored.'
      }
      return 'This entry could not be sent, and resending it will not help.'
    case 403:
      return 'You no longer have permission to send this.'
    case 404:
      return 'This kind of entry is no longer recognised by the server — it may be from an older version of the app.'
    case 409:
      return 'This entry conflicts with something already recorded and cannot be sent as it is.'
    case 422:
      return 'This entry is no longer valid and cannot be sent as it is.'
    case 426:
      return 'This entry needs a newer version of the app installed before it can be sent.'
    default:
      return 'This entry could not be sent, and resending it will not help.'
  }
}

/**
 * Doubling from 30 seconds, capped at 30 minutes (`backoffMs`, below) —
 * generous enough that a brief 5xx blip or a short rate-limit window clears
 * well inside it, short enough that a device back online after a genuinely
 * long outage (or a fresh sign-in clearing a 401) is not left waiting an
 * unreasonable time for the NEXT `online`/`visibilitychange` trigger to
 * actually retry.
 */
const RETRY_BASE_MS = 30_000
const RETRY_MAX_MS = 30 * 60_000

function backoffMs(attempts: number): number {
  return Math.min(RETRY_MAX_MS, RETRY_BASE_MS * 2 ** (attempts - 1))
}

/**
 * The recoverable-failure counterpart to `markRefused` — the row stays
 * `queued` (a human should not have to "repair" something that may well
 * succeed on its own once the underlying condition clears), but records
 * `attempts`/`retryAfter` so the NEXT `sweep()` does not immediately retry
 * it. Final review bucket 1 item 5, restating Task 17's own "one poison
 * entry loops forever" concern for this status class: an `online` +
 * `visibilitychange` pair can both fire on a single reconnect (`drain()`'s
 * own `inFlight` guard comment), and a device stuck behind a 401 would
 * otherwise resend the SAME request on every subsequent trigger too,
 * forever — wasteful for a session that stays expired, actively
 * counterproductive for a 429 specifically (retrying a rate limit
 * immediately makes it worse, not better).
 *
 * `Date.now()` here, not an injected `now` — `packages/web` is neither
 * `core` (CLAUDE.md: pure, no wall clock) nor `edge` (the one place
 * `Date.now()` is named as legal); this file's own sibling `api/hooks.ts`
 * already calls the wall clock directly for the identical reason
 * (`enqueuedAt: new Date().toISOString()`), and nothing here needs a fake
 * clock injected from outside a test.
 */
async function markRetry(entry: OutboxEntry): Promise<void> {
  const attempts = (entry.attempts ?? 0) + 1
  const retryAfter = new Date(Date.now() + backoffMs(attempts)).toISOString()
  await outboxDb().entries.put({ ...entry, attempts, retryAfter })
}

/**
 * `sweep()`'s own backoff gate — `true` while a PREVIOUS recoverable
 * failure's `retryAfter` (`markRetry`, above) has not yet elapsed. A row
 * with no `retryAfter` at all (never failed recoverably, or already sent)
 * is never backing off.
 */
function isBackingOff(entry: OutboxEntry): boolean {
  return entry.retryAfter !== undefined && Date.parse(entry.retryAfter) > Date.now()
}

/**
 * The listeners `startOutboxAutoDrain` attaches, or `null` if none are —
 * doubling as both the "already attached" guard AND the stored teardown, so
 * a second call while already attached returns the SAME real teardown
 * (idempotent) rather than a fresh no-op one a caller could be fooled into
 * relying on.
 */
let detachTriggers: (() => void) | null = null

/**
 * Wires `drain()` to run on reconnect (architecture §8: "replayed on
 * reconnect"), with NO Background Sync — task brief: "it does not exist on
 * WebKit, and site managers are on iPhones". Two DOM events instead, both
 * supported everywhere this app runs:
 *
 *  - `online` on `window` — the direct signal.
 *  - `visibilitychange` to `'visible'` on `document` — catches the case
 *    `online` misses: a site manager who put their phone away mid-drive
 *    through a dead zone and only reopens the tab once signal is already
 *    back, with no `offline`→`online` transition for THIS tab to observe
 *    (the browser may not fire `online` at all if it was never told this
 *    tab went `offline` in the first place). Same reasoning
 *    `useUpdatePrompt.ts` already gives for using this event over a plain
 *    `focus` listener.
 *
 * Called once from `main.tsx`, next to `installGlobalErrorReporting()` —
 * review round 1 moved it here FROM `api/hooks.ts`'s own module scope
 * (measured unsound: a test file importing only `@/api/hooks` and
 * dispatching these two events produced unhandled `MissingAPIError`
 * rejections with nothing to catch them — the suite stayed green only
 * because no such test existed yet). `main.tsx` is this codebase's own
 * established home for a once-per-page-load side effect that needs no
 * component to trigger it; `hooks.ts` is a module every screen happens to
 * import, not a deliberate boot sequence.
 *
 * Returns a teardown, mirroring `useUpdatePrompt`'s own effect-cleanup
 * shape, for symmetry and so a test can detach it deterministically rather
 * than leaking real `window`/`document` listeners past its own scope.
 */
export function startOutboxAutoDrain(): () => void {
  if (detachTriggers) return detachTriggers

  // Review round 1's Important 4 (fixed here after being measured): `void
  // drain()` with no `.catch` left a rejected sweep (most likely
  // `pending()`/`outboxDb()` itself rejecting — no working IndexedDB at
  // all) as an UNHANDLED REJECTION. `api/report.ts` installs a GLOBAL
  // `unhandledrejection` handler that POSTs every one of those to
  // `/api/client-error` with NO throttle, so a broken store turned every
  // reconnect trigger into an error report, forever. Swallowed here for the
  // same reason `useUpdatePrompt.ts`'s own `pollForUpdate` swallows
  // `update()` rejecting: best-effort, and not this listener's problem to
  // surface — the next trigger tries again regardless, and a device with a
  // genuinely broken store has no retry that would ever succeed anyway.
  const onOnline = (): void => {
    void drain().catch(() => undefined)
  }
  const onVisibilityChange = (): void => {
    if (document.visibilityState === 'visible') void drain().catch(() => undefined)
  }

  window.addEventListener('online', onOnline)
  document.addEventListener('visibilitychange', onVisibilityChange)

  const teardown = (): void => {
    window.removeEventListener('online', onOnline)
    document.removeEventListener('visibilitychange', onVisibilityChange)
    detachTriggers = null
  }
  detachTriggers = teardown
  return teardown
}

/**
 * Review round 1's Important 3 (fixed here after being measured):
 * `detachTriggers` (and `inFlight`, above) is module-scope state with
 * nothing else resetting it between tests — the same "six other modules in
 * this codebase already carry a `resetXForTests`" precedent this task's own
 * brief names. `test/outbox.test.tsx` calls this so a test that attaches
 * its own listeners via `startOutboxAutoDrain()` (review round 1 moved the
 * real call to `main.tsx`, which no test ever imports) tears them down
 * again rather than leaking real `window`/`document` listeners into the
 * next test in the same file.
 *
 * DETACH, NOT RESET — renamed at web-pre-deploy item 10, which found
 * `resetXForTests` carrying three different contracts in `packages/web`
 * against a codebase-wide convention (seven instances in `core`/`contracts`)
 * that the name means a plain, pure, one-way clear of a module's own data.
 * This function is not that, in a way that matters to its caller: clearing
 * `inFlight` DETACHES a sweep that may still be running — the promise is
 * dropped, not awaited and not cancelled, so its remaining `await
 * runCommand(...)` calls can still land, still write to Dexie and still
 * `noteRowRemoved()` AFTER this function has returned. A reader who assumed
 * the `core`/`contracts` contract would expect the module to be quiescent on
 * the next line, and it may not be. Nothing here can fix that — a sweep
 * mid-flight has no cancellation token to pull, and inventing one for a
 * test-only hook would put real machinery in the production path — so the
 * name is what carries the warning. A caller that genuinely needs the sweep
 * finished should `await drain()` before calling this. That detached sweep
 * also still HOLDS the cross-tab lock (`exclusively`, above) until it settles,
 * so a `drain()` on the next line can be handed `null` rather than a result.
 */
export function detachDrainForTests(): void {
  inFlight = null
  detachTriggers?.()
}
